import * as React from "react";
import { FaBookOpen, FaHome, FaPencilAlt, FaRegFileAlt, FaTags } from "react-icons/fa";
import { Link } from "react-router-dom";
import styled from "styled-components";

import colors from "../../../../../styles/colors";
import { composeView, courseAssessmentViews, courseUnitViews } from "../../../../../utils/courseViews";
import { PreferredLocale } from "../../../../../utils/locales";
import {
  CourseInstructionType,
  CourseView,
  ICourseEditOperations,
  ICourseNavigationOperations,
  ICourseUnit,
  IFullCourse,
  Slide,
} from "../../../../course-author/types";
import ClickableElement from "../../../../utils/ClickableElement";
import { instructions } from "../util";
import { InstructionalItemsCol, InstructionalItemsRow } from "./Grid";
import InstructionalMaterialGroup from "./InstructionalMaterialGroup";
import { InstructionalItemRow } from "./InstructionListItem";

interface IUnitContentDropDownContentProps {
  course: IFullCourse;
  courseId: string;
  display: boolean;
  editOperations: ICourseEditOperations;
  locale: PreferredLocale;
  maxTitleWidth: number;
  navigationOperations: ICourseNavigationOperations;
  unit: ICourseUnit;
  unitPath: Array<CourseView<ICourseUnit, IFullCourse>>;
  userId: string;
  alternateColor: boolean;
  unitUrl: string;
}

interface IDropdownContainerProps {
  alternateColor: boolean;
  shouldDisplay: boolean;
}

const DropdownContainer = styled.div<IDropdownContainerProps>(
  ({ theme, alternateColor, shouldDisplay }) => `
  background: ${backgroundColor(alternateColor)};
  display: ${shouldDisplay ? "inherit" : "none"};
  font-family: ${theme.fonts.OpenSans};
  font-size: ${theme.fontSizes.small};
  margin-left: 2.5rem;
  width: calc(100% - 2.5rem);
  padding-top: 0.5rem;
  padding-bottom: 1rem;
`
);

const DropdownLink = styled(Link)`
  align-items: center;
  color: inherit;
  display: flex;
  text-decoration: none;
  width: 100%;
  cursor: pointer;
`;

function backgroundColor(shouldAlternateColor: boolean) {
  return shouldAlternateColor ? colors.highlightLightBlue : colors.highlightLightGrey;
}

export const UnitContentDropdown: React.SFC<IUnitContentDropDownContentProps> = props => {
  const questions = composeView(courseAssessmentViews.questions, courseUnitViews.assessment)(props.unit) || [];
  const numQuestions: number = questions.filter((q: any) => !q.deleted).length;
  const instructionalMaterials = instructions(props.unit);
  const navigate = (comp: Slide) => () => {
    props.navigationOperations.setPath(props.unitPath);
    props.navigationOperations.setDisplayedComponent(comp)();
  };
  const { unitUrl } = props;
  const instructionalGroupTitleWidth = props.maxTitleWidth - 2.5;
  return (
    <DropdownContainer shouldDisplay={props.display} alternateColor={props.alternateColor}>
      <InstructionalItemsCol>
        <InstructionalItemsRow>
          <FaPencilAlt />
          <InstructionalItemsCol>
            <InstructionalItemRow>
              <DropdownLink to={`${unitUrl}.test`} onClick={navigate(props.unit.content.test)}>
                <ClickableElement>Assessment ({numQuestions})</ClickableElement>
              </DropdownLink>
            </InstructionalItemRow>
          </InstructionalItemsCol>
        </InstructionalItemsRow>
        <InstructionalMaterialGroup
          course={props.course}
          courseId={props.courseId}
          editOperations={props.editOperations}
          Icon={FaHome}
          instructionalMaterials={instructionalMaterials}
          instructionType={CourseInstructionType.Introduction}
          locale={props.locale}
          maxTitleWidth={instructionalGroupTitleWidth}
          navigationOperations={props.navigationOperations}
          unitPath={props.unitPath}
          unitUrl={unitUrl}
          bgColor={backgroundColor(props.alternateColor)}
        />
        <InstructionalMaterialGroup
          course={props.course}
          courseId={props.courseId}
          editOperations={props.editOperations}
          Icon={FaBookOpen}
          instructionalMaterials={instructionalMaterials}
          instructionType={CourseInstructionType.Lesson}
          locale={props.locale}
          maxTitleWidth={instructionalGroupTitleWidth}
          navigationOperations={props.navigationOperations}
          unitPath={props.unitPath}
          unitUrl={unitUrl}
          bgColor={backgroundColor(props.alternateColor)}
        />
        <InstructionalMaterialGroup
          course={props.course}
          courseId={props.courseId}
          editOperations={props.editOperations}
          Icon={FaRegFileAlt}
          instructionalMaterials={instructionalMaterials}
          instructionType={CourseInstructionType.Summary}
          locale={props.locale}
          maxTitleWidth={instructionalGroupTitleWidth}
          navigationOperations={props.navigationOperations}
          unitPath={props.unitPath}
          unitUrl={unitUrl}
          bgColor={backgroundColor(props.alternateColor)}
        />
        <InstructionalMaterialGroup
          course={props.course}
          courseId={props.courseId}
          editOperations={props.editOperations}
          Icon={FaTags}
          instructionalMaterials={instructionalMaterials}
          instructionType={CourseInstructionType.Appendix}
          locale={props.locale}
          maxTitleWidth={instructionalGroupTitleWidth}
          navigationOperations={props.navigationOperations}
          unitPath={props.unitPath}
          unitUrl={unitUrl}
          bgColor={backgroundColor(props.alternateColor)}
        />
      </InstructionalItemsCol>
    </DropdownContainer>
  );
};
